import React from 'react'
import { Box, Paper, CircularProgress, Typography } from '@material-ui/core'
import { useHistory, useParams } from 'react-router-dom'
import { useQuery, useMutation, queryCache } from 'react-query'
import { useSnackbar } from 'notistack'
import Button from 'UI/Button/ButtonWithPreloader'
import ChangeLogForm from 'components/ChangeLogForm'
import { getChangeLogEntry, getChangeLogRoles, updateChangeLogEntry } from 'services/changeLog'
import { IChangeLogForm } from 'typescript/interfaces/changeLog'
import { CHANGE_LOG_MANAGE_URL } from 'constants/routes'
import { SOMETHING_WENT_WRONG } from 'constants/errors'

const ChangeLogEdit = () => {
  const history = useHistory()
  const { id } = useParams<{ id: string }>()
  const entryId = Number(id)
  const { enqueueSnackbar } = useSnackbar()

  const { data: roles, isLoading: isLoadingRoles } = useQuery('changelog-roles', getChangeLogRoles)
  const { data: entry, isLoading: isLoadingEntry, isError } = useQuery(['changelog-entry', entryId], () => getChangeLogEntry(entryId))

  const [updateEntryMut, { isLoading }] = useMutation(updateChangeLogEntry, {
    onSuccess: () => {
      queryCache.invalidateQueries(['changelog-entry', entryId])
      queryCache.invalidateQueries('changelog-entries')
      queryCache.invalidateQueries('changelog-history')
      enqueueSnackbar('Change log entry successfully updated')
      history.push(CHANGE_LOG_MANAGE_URL)
    },
    onError: () => enqueueSnackbar(SOMETHING_WENT_WRONG),
  })

  if (isLoadingEntry || isLoadingRoles) {
    return (
      <Box display="flex" justifyContent="center" p="4rem">
        <CircularProgress color="primary" />
      </Box>
    )
  }

  if (isError || !entry) {
    return (
      <Box component={Paper} p="3rem" mb="2rem" display="flex" flexDirection="column" alignItems="center">
        <Typography variant="h3" color="textSecondary">
          This entry could not be loaded. It may have been deleted or is temporarily unavailable.
        </Typography>
        <Box mt="2rem">
          <Button color="primary" variant="contained" onClick={() => history.push(CHANGE_LOG_MANAGE_URL)}>
            Back to What&apos;s New Entries
          </Button>
        </Box>
      </Box>
    )
  }

  const availableRoles = roles ? roles.data : []

  const initValues: IChangeLogForm = {
    name: entry.data.name,
    releaseDate: entry.data.releaseDate,
    roleContents: availableRoles.map((role) => {
      const content = entry.data.roleContents.find((roleContent) => roleContent.role === role)
      return {
        role,
        bodyHtml: content ? content.bodyHtml || '' : '',
        isEnabled: content ? content.isEnabled : false,
      }
    }),
  }

  return (
    <Box component={Paper} p="3rem" mb="2rem">
      <Box mb="1.5rem">
        <Button variant="text" color="primary" onClick={() => history.push(CHANGE_LOG_MANAGE_URL)}>
          ← Back
        </Button>
      </Box>
      <ChangeLogForm
        heading="Edit What's New Entry"
        roles={availableRoles}
        initValues={initValues}
        loading={isLoading}
        onSubmit={(values) => updateEntryMut({ id: entryId, values })}
        onCancel={() => history.push(CHANGE_LOG_MANAGE_URL)}
      />
    </Box>
  )
}

export default ChangeLogEdit
